/**
 * Utility condivise dalla pagina /blog (hub) e dai post: formattazione date,
 * troncamento delle descrizioni meta e fusione dei feed esterni in un unico
 * elenco cronologico. Tutto puro e senza accesso a rete o DOM, quindi
 * testabile e identico tra prerender e client.
 */

export type StreamSource = "hwupgrade" | "substack";

export interface StreamItem {
    title: string;
    url: string;
    date: string | null;
    excerpt: string;
    source: StreamSource;
}

interface RawFeedItem {
    title: string;
    url: string;
    date?: string | null;
    excerpt?: string;
}

export interface RawFeeds {
    hwupgrade?: RawFeedItem[];
    substack?: RawFeedItem[];
}

export const SOURCE_LABEL: Record<StreamSource, string> = {
    hwupgrade: "IA Upgrade · HWUpgrade",
    substack: "Substack · Umani per ora",
};

/**
 * Data in italiano, sempre nel fuso di Roma. Il prerender gira su una
 * macchina con un fuso e il lettore ne ha un altro: senza fuso esplicito una
 * data vicina a mezzanotte UTC dà testi diversi tra HTML statico e primo
 * render client, cioè un mismatch di idratazione.
 */
export function formatDateIt(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("it-IT", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "Europe/Rome",
    });
}

/** Valore per l'attributo dateTime di <time>: solo la data, nel fuso di Roma. */
export function isoDay(dateStr: string | null | undefined): string | undefined {
    if (!dateStr) return undefined;
    const parts = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "Europe/Rome",
    }).format(new Date(dateStr));
    return parts;
}

const SITE = "https://www.ballerano.com";

/**
 * Serializza un oggetto JSON-LD per un <script type="application/ld+json">.
 * Il "<" viene scritto come \u003c: un titolo contenente "</script>" non può
 * chiudere il tag e iniettare markup.
 */
export function jsonLdString(data: unknown): string {
    return JSON.stringify(data).replace(/</g, "\\u003c");
}

/** URL assoluto sul dominio canonico (con www) a partire da un path del sito. */
export function absoluteUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    return `${SITE}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * Sceglie fino a `n` post da proporre in fondo a un articolo: prima quelli
 * che condividono più tag (due punti per tag), poi quelli della stessa
 * categoria (un punto), a parità il più recente. Se nessuno ha punti in
 * comune restano comunque i più recenti: con un blog piccolo un link in più
 * vale più di una sezione vuota. L'input è già ordinato dal più recente.
 */
export function pickRelated<T extends { slug: string; tags: string[]; category: string }>(
    current: { slug: string; tags: string[]; category: string },
    all: T[],
    n = 3,
): T[] {
    const mine = new Set(current.tags.map((t) => t.toLowerCase()));
    return all
        .filter((p) => p.slug !== current.slug)
        .map((p, order) => {
            const shared = p.tags.filter((t) => mine.has(t.toLowerCase())).length;
            const score = shared * 2 + (p.category === current.category ? 1 : 0);
            return { p, score, order };
        })
        .sort((a, b) => b.score - a.score || a.order - b.order)
        .slice(0, n)
        .map(({ p }) => p);
}

/**
 * Accorcia a `max` caratteri tagliando su un confine di parola e aggiunge
 * i puntini. Serve per le meta description (Google ne mostra circa 155).
 */
export function clip(text: string, max: number): string {
    const clean = text.replace(/\s+/g, " ").trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max - 1);
    const lastSpace = cut.lastIndexOf(" ");
    const base = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
    return `${base.replace(/[\s,;:.\-–—]+$/, "")}…`;
}

/** Chiave per riconoscere lo stesso pezzo pubblicato su due fonti: titolo senza maiuscole né punteggiatura. */
function titleKey(title: string): string {
    return title
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/**
 * Fonde i feed esterni in un solo elenco dal più recente. A parità di data
 * resta l'ordine di arrivo (HWUpgrade prima di Substack); le voci senza data
 * vanno in fondo. Un pezzo pubblicato con lo stesso titolo su entrambe le
 * fonti compare una volta sola, con il link della prima fonte (HWUpgrade).
 */
export function buildStream(raw: RawFeeds, limit = 10): StreamItem[] {
    const tag = (items: RawFeedItem[] | undefined, source: StreamSource): StreamItem[] =>
        (items ?? []).map((i) => ({
            title: i.title,
            url: i.url,
            date: i.date ?? null,
            excerpt: i.excerpt ?? "",
            source,
        }));

    const seen = new Set<string>();
    const all = [...tag(raw.hwupgrade, "hwupgrade"), ...tag(raw.substack, "substack")].filter((item) => {
        const key = titleKey(item.title);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
    return all
        .map((item, order) => ({ item, order }))
        .sort((a, b) => {
            const ta = a.item.date ? Date.parse(a.item.date) : -Infinity;
            const tb = b.item.date ? Date.parse(b.item.date) : -Infinity;
            if (ta !== tb) return tb - ta;
            return a.order - b.order;
        })
        .slice(0, limit)
        .map(({ item }) => item);
}
